import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { useEffect, useRef, useState } from 'react';

/**
 * Which of `rooms` are end-to-end encrypted (agora#135). `ChatRoomSummary`
 * carries no such flag; the answer is `CryptoApi.isEncryptionEnabledInRoom`.
 * A room already confirmed on is kept and never re-asked, since a room is
 * never un-encrypted -- but a room still off keeps being asked on every
 * call (a room can turn encryption on after it was first seen off), rather
 * than being marked "asked" forever the moment the first answer comes back.
 * Empty for a client whose crypto is off.
 */
export function useEncryptedRoomIds(
  client: ChatClient,
  rooms: readonly ChatRoomSummary[],
  enabled = true,
): ReadonlySet<string> {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  const known = useRef(new Set<string>());
  const asking = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) return;
    let crypto: ReturnType<ChatClient['getCrypto']>;
    try {
      crypto = client.getCrypto();
    } catch {
      return;
    }
    if (!crypto) return;
    for (const room of rooms) {
      if (known.current.has(room.id) || asking.current.has(room.id)) continue;
      asking.current.add(room.id);
      crypto
        .isEncryptionEnabledInRoom(room.id)
        .then(
          (on) => {
            if (!on) return;
            known.current.add(room.id);
            setIds((prev) => new Set(prev).add(room.id));
          },
          () => {},
        )
        .finally(() => asking.current.delete(room.id));
    }
  }, [client, rooms, enabled]);

  return ids;
}

/** Said in the room head of an encrypted room. The search and file limits
 *  are the SDK's own (`ChatClient.search`, `ChatClient.roomFiles`). */
export const ENCRYPTED_ROOM_NOTICE =
  'This room is end-to-end encrypted. Server-side search does not cover it, and its files do not appear in the Files list.';

/**
 * Deletes this browser's crypto databases for `storeName` (the company id),
 * so a signed-out browser keeps no keys. The names are the SDK's own
 * `cryptoStoreDbPrefix` (`wtfalch-messaging-crypto-<store>-<user>-<device>`),
 * which 0.7.0 exposes no call to delete. Best effort: a browser without
 * `indexedDB.databases()` keeps them.
 */
export async function deleteCryptoStores(storeName: string): Promise<void> {
  try {
    const prefix = `wtfalch-messaging-crypto-${storeName}-`;
    const dbs = await indexedDB.databases();
    await Promise.all(
      dbs
        .map((db) => db.name)
        .filter((name): name is string => typeof name === 'string' && name.startsWith(prefix))
        .map(
          (name) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(name);
              // Not on `blocked`: the delete stays queued until the chat client
              // closes the store, then completes.
              req.onsuccess = req.onerror = () => resolve();
            }),
        ),
    );
  } catch {
    // Nothing more can be done from here.
  }
}
